export type FontFamilyId =
    | 'inter'
    | 'source'
    | 'ibm-plex'
    | 'plus-jakarta'
    | 'system'
    | 'atkinson'
    | 'public-sans'
    | 'noto'
    | 'dm-sans'
    | 'manrope'
    | 'figtree'
    | 'work-sans'
    | 'libre'
    | 'lexend';

export const FONT_STORAGE_KEY = 'yoda-font';
export const FONT_STYLESHEET_ID = 'yoda-google-fonts';
export const FONT_CATALOG_STYLESHEET_ID = 'yoda-google-fonts-catalog';
export const DEFAULT_FONT_FAMILY: FontFamilyId = 'source';

export type FontPack = {
    id: FontFamilyId;
    /** Recognizable display name (brand / product name). */
    name: string;
    /** CSS font-family used in the appearance picker preview. */
    previewFamily: string;
    /** Google Fonts `family=` query parts. Empty = no remote fonts. */
    googleFamilies: string[];
};

const SANS_FALLBACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';
const SERIF_FALLBACK = 'Georgia, "Times New Roman", serif';
const MONO_FALLBACK = 'Menlo, Monaco, Consolas, "Courier New", monospace';

/** CSS stacks applied via data-yoda-font (must stay in sync with index.css). */
export const FONT_PACKS: FontPack[] = [
    { id: 'inter', name: 'Inter', previewFamily: 'Inter, sans-serif', googleFamilies: ['Ubuntu:ital,wght@0,400;0,500;0,700;1,400'] },
    { id: 'source', name: 'Source', previewFamily: '"Source Sans 3", sans-serif', googleFamilies: [
        'Source+Sans+3:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,600',
        'Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;0,8..60,700;1,8..60,400',
        'Source+Code+Pro:ital,wght@0,400;0,500;0,600;0,700;1,400',
    ] },
    { id: 'ibm-plex', name: 'IBM Plex', previewFamily: '"IBM Plex Sans", sans-serif', googleFamilies: [
        'IBM+Plex+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400',
        'IBM+Plex+Serif:ital,wght@0,400;0,600;0,700;1,400',
        'IBM+Plex+Mono:ital,wght@0,400;0,500;0,600;0,700;1,400',
    ] },
    { id: 'plus-jakarta', name: 'Plus Jakarta', previewFamily: '"Plus Jakarta Sans", sans-serif', googleFamilies: [
        'Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400',
        'JetBrains+Mono:ital,wght@0,400;0,500;0,600;0,700;1,400',
    ] },
    { id: 'system', name: 'System', previewFamily: 'system-ui, sans-serif', googleFamilies: [] },
    { id: 'atkinson', name: 'Atkinson', previewFamily: '"Atkinson Hyperlegible", sans-serif', googleFamilies: [
        'Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400;1,700',
        'JetBrains+Mono:ital,wght@0,400;0,500;0,600;0,700;1,400',
    ] },
    { id: 'public-sans', name: 'Public Sans', previewFamily: '"Public Sans", sans-serif', googleFamilies: [
        'Public+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400',
        'Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;0,8..60,700;1,8..60,400',
        'Source+Code+Pro:ital,wght@0,400;0,500;0,600;0,700;1,400',
    ] },
    { id: 'noto', name: 'Noto', previewFamily: '"Noto Sans", sans-serif', googleFamilies: [
        'Noto+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400',
        'Noto+Serif:ital,wght@0,400;0,600;0,700;1,400',
        'Noto+Sans+Mono:wght@400;500;600;700',
    ] },
    { id: 'dm-sans', name: 'DM Sans', previewFamily: '"DM Sans", sans-serif', googleFamilies: [
        'DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400',
        'DM+Serif+Text:ital@0;1',
        'DM+Mono:ital,wght@0,400;0,500;1,400',
    ] },
    { id: 'manrope', name: 'Manrope', previewFamily: 'Manrope, sans-serif', googleFamilies: [
        'Manrope:wght@300;400;500;600;700',
        'Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;0,8..60,700;1,8..60,400',
        'JetBrains+Mono:ital,wght@0,400;0,500;0,600;0,700;1,400',
    ] },
    { id: 'figtree', name: 'Figtree', previewFamily: 'Figtree, sans-serif', googleFamilies: [
        'Figtree:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400',
        'Lora:ital,wght@0,400;0,600;0,700;1,400',
        'JetBrains+Mono:ital,wght@0,400;0,500;0,600;0,700;1,400',
    ] },
    { id: 'work-sans', name: 'Work Sans', previewFamily: '"Work Sans", sans-serif', googleFamilies: [
        'Work+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400',
        'Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;0,8..60,700;1,8..60,400',
        'Source+Code+Pro:ital,wght@0,400;0,500;0,600;0,700;1,400',
    ] },
    { id: 'libre', name: 'Libre', previewFamily: '"Libre Franklin", sans-serif', googleFamilies: [
        'Libre+Franklin:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400',
        'Libre+Baskerville:ital,wght@0,400;0,700;1,400',
        'Fira+Code:wght@400;500;600;700',
    ] },
    { id: 'lexend', name: 'Lexend', previewFamily: 'Lexend, sans-serif', googleFamilies: [
        'Lexend:wght@300;400;500;600;700',
        'Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;0,8..60,700;1,8..60,400',
        'JetBrains+Mono:ital,wght@0,400;0,500;0,600;0,700;1,400',
    ] },
];

const FONT_IDS = new Set<string>(FONT_PACKS.map((p) => p.id));

export function normalizeFontFamily(value?: string | null): FontFamilyId {
    if (value && FONT_IDS.has(value))
        return value as FontFamilyId;
    return DEFAULT_FONT_FAMILY;
}

export function getFontPack(id?: string | null): FontPack {
    const normalized = normalizeFontFamily(id);
    return FONT_PACKS.find((p) => p.id === normalized) || FONT_PACKS.find((p) => p.id === DEFAULT_FONT_FAMILY)!;
}

export function googleFontsHref(pack: FontPack): string | null {
    if (!pack.googleFamilies.length)
        return null;
    return `https://fonts.googleapis.com/css2?${pack.googleFamilies.map((f) => `family=${f}`).join('&')}&display=swap`;
}

export function readStoredFontFamily(): FontFamilyId {
    try {
        return normalizeFontFamily(localStorage.getItem(FONT_STORAGE_KEY));
    } catch {
        return DEFAULT_FONT_FAMILY;
    }
}

export function storeFontFamily(fontFamily: FontFamilyId): void {
    try {
        localStorage.setItem(FONT_STORAGE_KEY, normalizeFontFamily(fontFamily));
    } catch {
        // ignore quota / private mode
    }
}

function ensureStylesheetLink(id: string, href: string | null): void {
    if (typeof document === 'undefined')
        return;
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!href) {
        if (link)
            link.remove();
        return;
    }
    if (!link) {
        link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
    }
    if (link.getAttribute('href') !== href)
        link.setAttribute('href', href);
}

/** Prefetch every Google family used by packs (appearance picker previews). */
export function loadFontCatalog(): void {
    const families = Array.from(new Set(FONT_PACKS.flatMap((p) => p.googleFamilies)));
    if (!families.length)
        return;
    ensureStylesheetLink(
        FONT_CATALOG_STYLESHEET_ID,
        `https://fonts.googleapis.com/css2?${families.map((f) => `family=${f}`).join('&')}&display=swap`,
    );
}

/** Apply selected font pack to <html data-yoda-font> and load remote stylesheets. */
export function applyFontFamily(fontFamily: FontFamilyId | string): void {
    if (typeof document === 'undefined')
        return;
    const pack = getFontPack(fontFamily);
    document.documentElement.setAttribute('data-yoda-font', pack.id);
    ensureStylesheetLink(FONT_STYLESHEET_ID, googleFontsHref(pack));
}

/** Exposed for tests / docs — fallback stacks used in CSS. */
export const FONT_FALLBACKS = { SANS_FALLBACK, SERIF_FALLBACK, MONO_FALLBACK };
